import React, { useState } from "react";
import { Card, CardBody, Button, Row, Col } from "reactstrap";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend, PieChart, Pie, Cell } from "recharts";
import { Funnel } from "@ant-design/charts";
import _ from "lodash";
import moment from "moment";

const DashboardCard = ({ hits, workFlow, course }) => {
  if (!hits || hits.length === 0) return <p>No hay postulantes para mostrar</p>;

  const workflowOrder = workFlow.map(step => step.name);
  const lastStage = workflowOrder.at(-1) || null;
  const totalVacantes = course?.vacancies || 0;
  const totalPostulantes = hits.length;

  let totalEnProceso = 0, totalRechazados = 0, totalContratados = 0;
  const stageCounts = {}, rejectedCounts = {}, timeData = {};

  hits.forEach(hit => {
    const stages = hit?._source?.applicant?.enrollment?.stages || [];

    if (stages.length) {
      const isRejected = stages.some(stage => stage?.aasm_state === "rejected");

      if (isRejected) {
        const rejectedStage = stages.find(stage => stage?.aasm_state === "rejected");
        if (rejectedStage) {
          rejectedCounts[rejectedStage.name] = (rejectedCounts[rejectedStage.name] || 0) + 1;
        }
        totalRechazados++;
      } else {
        const isApproved = stages.every(stage => stage?.aasm_state !== "rejected");
        if (isApproved) totalEnProceso++;

        const lastStageReached = stages.some(stage => stage?.name === lastStage && stage?.aasm_state === "approved");
        if (lastStageReached) totalContratados++;

        const currentStage = stages.find(stage => stage?.is_current);
        if (currentStage) {
          stageCounts[currentStage.name] = (stageCounts[currentStage.name] || 0) + 1;
        }
      }

      stages.forEach(stage => {
        const duration = moment(stage.updated_at).diff(moment(stage.created_at), "days");
        if (!timeData[stage.name]) {
          timeData[stage.name] = { totalDays: 0, count: 0 };
        }
        timeData[stage.name].totalDays += duration;
        timeData[stage.name].count++;
      });
    }
  });

  const cuposLibres = Math.max(totalVacantes - totalContratados, 0);

  const genderCounts = _.countBy(hits, hit => hit?._source?.applicant?.user?.personal_information?.gender);
  const genderData = Object.entries(genderCounts).map(([key, value]) => ({
    name: key === "male" ? "Hombre" : key === "female" ? "Mujer" : "Otro",
    value
  }));
  const COLORS = ["#0088FE", "#00C49F", "#FFBB28"];

  const stageData = workflowOrder.map(stage => ({
    etapa: stage,
    postulantes: stageCounts[stage] || 0,
    rechazados: rejectedCounts[stage] || 0,
  }));

  const avgTimeData = workflowOrder
    .filter(stage => timeData[stage])
    .map(stage => ({
      etapa: stage,
      dias: Math.round(timeData[stage].totalDays / timeData[stage].count || 0),
  }));

  const funnelData = workflowOrder
    .filter(stage => stageCounts[stage] !== undefined).map(stage => ({
      etapa: stage,
      cantidad: stageCounts[stage],
  }));


  return (
    <Card className="mb-4">
      <CardBody>
        <Row className="mb-4">
          <h5>📌 Resumen de Postulaciones</h5>
          <div className="d-flex align-iems-center flex-nowrap justify-content-between">
            <Card className={`flex-fill m-1 p-2 text-center`}>
              <h6>Total Postulantes</h6>
              <h4>{totalPostulantes}</h4>
            </Card>
            {[
              { label: "En Proceso", value: totalEnProceso, className: "text-primary" },
              { label: "Rechazados", value: totalRechazados, className: "text-danger" },
              { label: "Contratados", value: totalContratados, className: "text-info" },
              { label: "Cupos Libres", value: cuposLibres, className: "text-warning" }
            ].map(({ label, value, className = "" }, index) => (
              <Card className={`flex-fill m-1 p-2 text-center ${className}`} key={index}>
                <h6>{label}</h6>
                <h4 className="fw-bold">{value}</h4>
              </Card>
            ))}
          </div>
        </Row>

        <Row className="mb-4">
          <Col md="6">
            <h5>🟢 Distribución por Género</h5>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={genderData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100}>
                  {genderData.map((_, index) => <Cell key={index} fill={COLORS[index % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Col>

          <Col md="6">
            <h5>📊 Postulantes por Etapa + Rechazados</h5>
            <ResponsiveContainer width="100%" height="90%">
              <BarChart layout="vertical" data={stageData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="etapa" />
                <Tooltip />
                <Legend />
                <Bar dataKey="postulantes" stackId="a" fill="#1890ff" name="Postulantes" />
                <Bar dataKey="rechazados" stackId="a" fill="#ff4d4f" name="Rechazados" />
              </BarChart>
            </ResponsiveContainer>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col md="6">
            <h5>⏳ Tiempo Promedio en Cada Etapa</h5>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={avgTimeData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="etapa" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="dias" fill="#ff9800" name="Días Promedio" />
              </BarChart>
            </ResponsiveContainer>
          </Col>

          <Col md="6">
            <h5>📉 Conversión Entre Etapas</h5>
            <Funnel data={funnelData} xField="etapa" yField="cantidad" color={["#4CAF50", "#FFC107", "#FF5722"]} />
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

export default DashboardCard;
